import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { useParams } from "react-router-dom";
// import MUI components
import { Grid2 as Grid } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { BoxCenterStart } from '../../style/box';
import { TypographyTitle } from '../../style/typography';
import { commonStyles } from "../../style/common";
import { RequestButton } from "../../style/button";
import SkeletonLoading from "../../components/skeleton_loading";
import { BaseDialog } from '../../components/base_dialog';
import { SuiroExtractRequiredPowerplantList } from '../../components/suiro_extract_required_powerplant_list';
import { SuiroExtractOptionalPowerplantList } from '../../components/suiro_extract_optional_powerplant_list';
import { fetchData } from "../../func/api";
import { useAuth } from '../../contexts/AuthContext';
import Alert from "@mui/material/Alert";
import { postData } from "../../func/api";
import Snackbar from '@mui/material/Snackbar';
import { BaseStepComponent, TaskStep } from '../../components/workflow_step';
import { POWERPLANT_TASK } from '../../const/inspection_texts';
import { ROLE_UUID, REPORT_STATUS } from "../../const/config";
import ReportStopDialog from "../../components/report_stop_dialog";

const PowerPlantWorkCheck = () => {
  const [open, setOpen] = useState(false);
  const [earthquake, setEarthquake] = useState({});
  const [suiroExtractRequiredList, setSuiroExtractRequiredList] = useState([]);
  const [suiroExtractOptionalList, setSuiroExtractOptionalList] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [checkData, setCheckData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifiedIntakeContact, setNotifiedIntakeContact] = useState('');
  const [notifiedIntakeError, setNotifiedIntakeError] = useState(false);
  const [notifiedIntakeOpen, setNotifiedIntakeOpen] = useState(false);
  const [drainageNoticeContact, setDrainageNoticeContact] = useState('');
  const [drainageNoticeError, setDrainageNoticeError] = useState(false);
  const [drainageNoticeOpen, setDrainageNoticeOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [containerHeight, setContainerHeight] = useState(850);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("");

  const { uuid } = useParams();
  const { currentUser } = useAuth();

  const isButtonDisabled =
    earthquake?.report_status_uuid === REPORT_STATUS.REPORTSTOP
    || (earthquake?.report_status_uuid === REPORT_STATUS.SECONDARYCHECK
      && earthquake?.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTCOMPLETE)
    || currentUser?.role_id === ROLE_UUID.VIEWER;

  // コンポーネント内（useParams の直後など）
  const allChecked = (list, field) => list.length > 0 && list.every(r => !!r[field]);

  const toggleColumn = useCallback((field, required) => {
    const all = allChecked(suiroExtractRequiredList, field);

    if (field === 'notifiedIntakeStop' || field === 'drainageNoticeSent') {
      const isIntake = field === 'notifiedIntakeStop';
      const contact = isIntake ? notifiedIntakeContact : drainageNoticeContact;
      const setError = isIntake ? setNotifiedIntakeError : setDrainageNoticeError;
      const setOpen = isIntake ? setNotifiedIntakeOpen : setDrainageNoticeOpen;

      if (required) {
        if (!all) {
          setSuiroExtractRequiredList(prev => prev.map(r => ({ ...r, [field]: true })));
          setCheckData(prev => prev.map(r => ({ ...r, [field]: true })));
          if (!contact) {
            setError(true);
            setOpen(true);
          } else {
            setError(false);
            setOpen(false);
          }
          return;
        } else {
          setSuiroExtractRequiredList(prev => prev.map(r => ({ ...r, [field]: false })));
          setCheckData(prev => prev.map(r => ({ ...r, [field]: false })));
          setError(false);
          setOpen(false);
          return;
        }
      } else {
        if (!all) {
          setSuiroExtractOptionalList(prev => prev.map(r => ({ ...r, [field]: true })));
          setCheckData(prev => prev.map(r => ({ ...r, [field]: true })));
          if (!contact) {
            setError(true);
            setOpen(true);
          } else {
            setError(false);
            setOpen(false);
          }
          return;
        } else {
          setSuiroExtractOptionalList(prev => prev.map(r => ({ ...r, [field]: false })));
          setCheckData(prev => prev.map(r => ({ ...r, [field]: false })));
          setError(false);
          setOpen(false);
          return;
        }
      }
    }

    if (required) {
      setSuiroExtractRequiredList(prev => prev.map(r => ({ ...r, [field]: !all })));
    } else {
      setSuiroExtractOptionalList(prev => prev.map(r => ({ ...r, [field]: !all })));
    }
    setCheckData(prev => prev.map(r => ({ ...r, [field]: !all })));
  }, [
    suiroExtractRequiredList,
    setSuiroExtractRequiredList,
    setSuiroExtractOptionalList,
    setCheckData,
    notifiedIntakeContact,
    drainageNoticeContact,
    setNotifiedIntakeError,
    setNotifiedIntakeOpen,
    setDrainageNoticeError,
    setDrainageNoticeOpen
  ]);

  // 固定列（リセット / 報告対象外 / 発電所名）の幅取得用
  const resetRef = useRef(null);
  const notReportRef = useRef(null);
  const nameRef = useRef(null);
  const [lefts, setLefts] = useState({ reset: 0, notReport: 0, name: 0 });
  const tableRef = useRef(null);

  useLayoutEffect(() => {
    const calc = () => {
      const rW = resetRef.current?.offsetWidth || 0;
      const nW = notReportRef.current?.offsetWidth || 0;
      const nameW = nameRef.current?.offsetWidth || 0;
      setLefts({ reset: 0, notReport: rW, name: rW + nW });

      const thead = tableRef.current?.querySelector('thead');
      const theadH = thead?.offsetHeight || 0;
      setHeaderHeight(theadH);

      // テーブルコンテナの利用可能高さを計算（ヘッダー・フッター等の余白を差し引く）
      const tableTop = tableRef.current?.getBoundingClientRect().top || 0;
      const bottomPadding = 160; // ページ下の余白やボタン領域に合わせて調整してください
      const avail = Math.max(200, window.innerHeight - tableTop - bottomPadding);
      setContainerHeight(avail);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // 発電所再開ボタンのハンドラー (ダミー)
  const handleRestartAllPowerplants = async () => {
    try {
      const response = await postData(`api/restart_all_powerplants/${uuid}`, {});
      if (response) {
        setOpen(false);
      } else {
        setOpen(false);
        setAlertMessage("全発電所発電再開処理の更新に失敗しました。時間をおいて再度お試しください。");
        setAlertOpen(true);
        setAlertSeverity("error");
      }
    } catch (e) {
      setOpen(false);
      setAlertMessage("全発電所発電再開処理の更新に失敗しました。時間をおいて再度お試しください。");
      setAlertOpen(true);
      setAlertSeverity("error");
    } finally {
      setAlertMessage("全発電所発電再開処理を完了しました。まもなく地震詳細ページに遷移します。");
      setAlertOpen(true);
      setAlertSeverity("success");
      setTimeout(() => {
        window.location.href = `/earthquake_detail/${uuid}`;
      }, 3000);
    }
  };

  // データ取得
  useEffect(() => {
    if (!uuid) return;
    const fetchAll = async () => {
      setLoading(true);
      try {
        // earthquake を先に取るのは同じ
        const earthquakeData = await fetchData(`api/get_earthquake/${uuid}`);
        const reportUuid = earthquakeData?.report_uuid || '';
        const [suiroExtractRequiredListData, suiroExtractOptionalListData, userList, partnerCompanyPersonList] = await Promise.all([
          fetchData(`api/get_powerplant_suiro_extract_required_list/true?report_uuid=${reportUuid}`),
          fetchData(`api/get_powerplant_suiro_extract_required_list/false?report_uuid=${reportUuid}`),
          fetchData(`api/get_user_list`),
          fetchData(`api/get_partner_company_person_select_list`)
        ]);
        // 地震データ取得
        setEarthquake(earthquakeData);
        // 水路抜水必須発電所一覧データ取得
        setSuiroExtractRequiredList(suiroExtractRequiredListData);
        // 水路抜水任意発電所一覧データ取得
        setSuiroExtractOptionalList(suiroExtractOptionalListData);
        // ユーザー一覧
        setUsers(userList);
        // 連絡先担当者一覧
        setPartners(partnerCompanyPersonList);

        // TODO: 実際のデータでcheckDataを初期化
        // setCheckData(powerplantDataFromAPI);
      } catch (e) {
        console.error('fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [uuid]);

  if (loading) return <SkeletonLoading />;

  return (
    <BoxCenterStart sx={commonStyles.page}>
      <Grid
        container
        spacing={4}
        columns={12}
        sx={commonStyles.container}
      >
        {/* ヘッダー部分 */}
        <Grid size={12}>
          {!isButtonDisabled && (
            <ReportStopDialog initial_report_uuid={earthquake.initial_report_uuid} earthquake_uuid={earthquake.uuid} />
          )}
          <TypographyTitle>{earthquake.name}</TypographyTitle>
        </Grid>

        <Grid size={12} sx={{ marginTop: 3 }}>
          <BaseStepComponent
            title={POWERPLANT_TASK.step1.title}
            lead={POWERPLANT_TASK.step1.lead}
          />
        </Grid>

        {/* 水路抜水必須発電所テーブル */}
        <SuiroExtractRequiredPowerplantList
          uuid={uuid}
          currentUser={currentUser}
          tableRef={tableRef}
          resetRef={resetRef}
          notReportRef={notReportRef}
          nameRef={nameRef}
          title="水路抜水必須発電所"
          lefts={lefts}
          headerHeight={headerHeight}
          suiroExtractList={suiroExtractRequiredList}
          setSuiroExtractList={setSuiroExtractRequiredList}
          setOtherSuiroExtractList={setSuiroExtractOptionalList}
          users={users}
          partners={partners}
          toggleColumn={toggleColumn}
        />
        {/* 水路抜水任意発電所テーブル */}
        <SuiroExtractOptionalPowerplantList
          uuid={uuid}
          currentUser={currentUser}
          tableRef={tableRef}
          resetRef={resetRef}
          notReportRef={notReportRef}
          nameRef={nameRef}
          title="水路抜水任意発電所"
          lefts={lefts}
          headerHeight={headerHeight}
          suiroExtractList={suiroExtractOptionalList}
          setSuiroExtractList={setSuiroExtractOptionalList}
          setOtherSuiroExtractList={setSuiroExtractRequiredList}
          users={users}
          partners={partners}
          toggleColumn={toggleColumn}
        />

        <Grid size={12} sx={{ marginTop: 5 }}>
          <TaskStep
            title={POWERPLANT_TASK.step2.title}
            instructions={[POWERPLANT_TASK.step2.lead]}
            onNext={() => setOpen(true)}
            buttonText={"全発電所発電再開"}
            startIcon={<BoltIcon />}
          />
        </Grid>
        <BaseDialog
          open={open}
          onClose={handleClose}
          title="全発電所発電再開確認"
          message={`全発電所発電再開しましたか？`}
          onConfirm={handleRestartAllPowerplants}
          confirmText='OK'
          cancelText='キャンセル'
        />
        <Snackbar
          open={alertOpen}
          autoHideDuration={3000}
          onClose={() => setAlertOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
            {alertMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </BoxCenterStart>
  );
};

export default PowerPlantWorkCheck;